import type {
  BackupJobConRelazioni,
  MacchinaServizioStato,
  Notifica,
  ServiceEventConRelazioni,
  TaskConRelazioni,
  TicketCommento,
  TicketConRelazioni,
  UUID,
} from './entities';

/**
 * Socket.IO contract. Event names are namespaced by domain so the gateway can
 * scope rooms per tenant; the backend joins each socket to `tenant:<id>` (or
 * `operators` for staff) on connection, using the JWT.
 */
export const RealtimeEvent = {
  TicketCreated: 'ticket.created',
  TicketUpdated: 'ticket.updated',
  TicketCommentCreated: 'ticket.comment.created',
  TaskCreated: 'task.created',
  TaskUpdated: 'task.updated',
  TaskPaused: 'task.paused',
  ServiceStateChanged: 'service.state.changed',
  BackupJobCompleted: 'backup.job.completed',
  BackupPendingCreated: 'backup.pending.created',
  BackupPendingResolved: 'backup.pending.resolved',
  ServiceEventCreated: 'service.event.created',
  NotificationCreated: 'notification.created',
} as const;

export type RealtimeEventName = (typeof RealtimeEvent)[keyof typeof RealtimeEvent];

export interface RealtimeEnvelope<T> {
  /** Emitting tenant; null for staff-wide broadcasts. */
  cliente_id: UUID | null;
  emitted_at: string;
  payload: T;
}

/** Maps each event name to its payload, so listeners are fully typed. */
export interface RealtimeEventPayloads {
  [RealtimeEvent.TicketCreated]: RealtimeEnvelope<TicketConRelazioni>;
  [RealtimeEvent.TicketUpdated]: RealtimeEnvelope<TicketConRelazioni>;
  [RealtimeEvent.TicketCommentCreated]: RealtimeEnvelope<TicketCommento>;
  [RealtimeEvent.TaskCreated]: RealtimeEnvelope<TaskConRelazioni>;
  [RealtimeEvent.TaskUpdated]: RealtimeEnvelope<TaskConRelazioni>;
  [RealtimeEvent.TaskPaused]: RealtimeEnvelope<TaskConRelazioni>;
  [RealtimeEvent.ServiceStateChanged]: RealtimeEnvelope<MacchinaServizioStato>;
  [RealtimeEvent.BackupJobCompleted]: RealtimeEnvelope<BackupJobConRelazioni>;
  [RealtimeEvent.BackupPendingCreated]: RealtimeEnvelope<{
    cliente_id: UUID;
    cliente_nome?: string | null;
    hint_macchina_nome: string;
    pending_count: number;
  }>;
  [RealtimeEvent.BackupPendingResolved]: RealtimeEnvelope<{
    cliente_id: UUID;
    hint_macchina_nome: string;
    remaining_count: number;
  }>;
  [RealtimeEvent.ServiceEventCreated]: RealtimeEnvelope<ServiceEventConRelazioni>;
  [RealtimeEvent.NotificationCreated]: RealtimeEnvelope<Notifica>;
}

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

/* ------------------------------------------------------------------ */
/* AI assistant streaming protocol                                    */
/* ------------------------------------------------------------------ */

export type ChatRole = 'user' | 'assistant' | 'system';

export interface ChatMessage {
  id: string;
  role: ChatRole;
  content: string;
  createdAt: string;
  /** True while tokens are still arriving for this message. */
  streaming?: boolean;
  error?: string;
  /** Records the assistant returned as grounding for its answer. */
  citations?: AssistantCitation[];
}

export interface AssistantCitation {
  kind: 'ticket' | 'backup_job' | 'service_event' | 'macchina' | 'contratto';
  id: UUID;
  label: string;
  /** In-app route the user can follow. */
  href?: string;
}

/**
 * A machine-readable action the assistant can propose, rendered as a button.
 * The frontend decides what each `type` does; the model only picks intent.
 */
export interface AssistantSuggestion {
  id: string;
  label: string;
  description?: string;
  action:
    | { type: 'open_ticket'; prefill: { oggetto: string; descrizione: string; priorita?: string; fonte?: string; macchina_id?: UUID } }
    | { type: 'navigate'; href: string }
    | { type: 'ask'; prompt: string };
}

/** Context we attach to every prompt so answers are grounded in live data. */
export interface AssistantContext {
  route: string;
  cliente_id: UUID | null;
  /** IDs the user is currently looking at, e.g. the open ticket. */
  entity?: { kind: AssistantCitation['kind']; id: UUID };
}

/** Server-sent event frames from `POST /ai/chat/stream`. */
export type AssistantStreamChunk =
  | { type: 'token'; value: string }
  | { type: 'citations'; value: AssistantCitation[] }
  | { type: 'suggestions'; value: AssistantSuggestion[] }
  | { type: 'error'; value: string }
  | { type: 'done' };
